import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import CtaSection from '@/components/CtaSection';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Calendar, Clock, Tag, Layers, CheckCircle, Image as ImageIcon } from 'lucide-react';
import { caseStudies } from '@/data/caseStudies';

// Define the type for case study data
interface CaseStudy {
  id: string;
  title: string;
  category: string;
  description: string;
  client: string;
  duration: string;
  date: string;
  challenge: string;
  solution: string;
  results: string[];
  technologies: string[];
  imageUrl: string;
  gallery: string[];
  testimonial?: {
    quote: string;
    author: string;
    position: string;
  };
  link: string;
}

const CaseStudy = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [caseStudy, setCaseStudy] = useState<CaseStudy | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  useEffect(() => {
    // Find the case study by ID
    const study = caseStudies.find(study => study.id === id);
    if (study) {
      setCaseStudy(study);
      // Scroll to top when case study changes
      window.scrollTo(0, 0);
    }
    setLoading(false);
  }, [id]);
  const getNextCaseStudy = () => {
    if (!caseStudy) return null;
    const currentIndex = caseStudies.findIndex(study => study.id === caseStudy.id);
    const nextIndex = (currentIndex + 1) % caseStudies.length;
    return caseStudies[nextIndex];
  };
  const getPreviousCaseStudy = () => {
    if (!caseStudy) return null;
    const currentIndex = caseStudies.findIndex(study => study.id === caseStudy.id);
    const previousIndex = (currentIndex - 1 + caseStudies.length) % caseStudies.length;
    return caseStudies[previousIndex];
  };
  const nextCaseStudy = getNextCaseStudy();
  const previousCaseStudy = getPreviousCaseStudy();
  const nextImage = () => {
    if (caseStudy) {
      setCurrentImageIndex((currentImageIndex + 1) % caseStudy.gallery.length);
    }
  };
  const prevImage = () => {
    if (caseStudy) {
      setCurrentImageIndex((currentImageIndex - 1 + caseStudy.gallery.length) % caseStudy.gallery.length);
    }
  };
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse">Loading case study...</div>
      </div>
    );
  }
  if (!caseStudy) {
    // Navigate to 404 page if case study not found
    useEffect(() => {
      navigate('/not-found');
    }, [navigate]);
    return null;
  }
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />

      <main className="flex-grow">
        {/* Hero Banner */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="flex items-center gap-2 mb-8">
              <Link to="/services/portfolio" className="text-luxury-600 hover:text-highlight transition-colors flex items-center gap-1" onClick={() => window.scrollTo(0, 0)}>
                <ArrowLeft size={16} />
                <span>Back to Portfolio</span>
              </Link>
              <Separator className="h-5" orientation="vertical" />
              <span className="text-highlight font-medium">{caseStudy.category}</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight text-luxury-900">
              {caseStudy.title}
            </h1>

            <p className="text-lg md:text-xl text-luxury-700 max-w-3xl mb-8">
              {caseStudy.description}
            </p>

            <div className="flex flex-wrap gap-8 mb-12">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-highlight" />
                <span className="text-luxury-700">Completed: {caseStudy.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-highlight" />
                <span className="text-luxury-700">Duration: {caseStudy.duration}</span>
              </div>
              <div className="flex items-center gap-2">
                <Tag size={18} className="text-highlight" />
                <span className="text-luxury-700">Client: {caseStudy.client}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Image */}
        <section className="py-10 md:py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="relative aspect-video rounded-xl overflow-hidden shadow-luxury mb-6">
              <img
                src={caseStudy.imageUrl}
                alt={caseStudy.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder.svg';
                }}
              />
            </div>
          </div>
        </section>

        {/* Content Details */}
        <section className="py-10 px-6 md:px-10 bg-white">
          <div className="container mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Main Content - Left 2/3 */}
              <div className="lg:col-span-2 space-y-12">
                {/* Challenge */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-luxury-900">The Challenge</h2>
                  <p className="text-luxury-800 text-lg leading-relaxed">
                    {caseStudy.challenge}
                  </p>
                </div>

                {/* Solution */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-luxury-900">Our Solution</h2>
                  <p className="text-luxury-800 text-lg leading-relaxed">
                    {caseStudy.solution}
                  </p>
                </div>

                {/* Results */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-luxury-900">The Results</h2>
                  <ul className="space-y-4">
                    {caseStudy.results.map((result, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle size={22} className="text-highlight mt-1 flex-shrink-0" />
                        <span className="text-luxury-800 text-lg">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Image Gallery */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6 text-luxury-900">Gallery</h2>

                  <div className="relative rounded-xl overflow-hidden aspect-video shadow-luxury mb-4">
                    {caseStudy.gallery.length > 0 ? (
                      <>
                        <img
                          src={caseStudy.gallery[currentImageIndex]}
                          alt={`${caseStudy.title} gallery image ${currentImageIndex + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/placeholder.svg';
                          }}
                        />
                        {caseStudy.gallery.length > 1 && (
                          <div className="absolute inset-0 flex items-center justify-between pointer-events-none">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10 rounded-full bg-white/80 text-luxury-900 shadow-md pointer-events-auto ml-4"
                              onClick={prevImage}
                            >
                              <ArrowLeft size={18} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-10 w-10 rounded-full bg-white/80 text-luxury-900 shadow-md pointer-events-auto mr-4"
                              onClick={nextImage}
                            >
                              <ArrowRight size={18} />
                            </Button>
                          </div>
                        )}

                        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                          {caseStudy.gallery.map((_, index) => (
                            <button
                              key={index}
                              className={`w-2 h-2 rounded-full ${index === currentImageIndex ? 'bg-highlight' : 'bg-white/70'
                                }`}
                              onClick={() => setCurrentImageIndex(index)}
                              aria-label={`View image ${index + 1}`}
                            />
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-luxury-100">
                        <div className="flex flex-col items-center text-luxury-400">
                          <ImageIcon size={48} />
                          <p className="mt-4">No gallery images available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Testimonial (if available) */}
                {caseStudy.testimonial && (
                  <div className="bg-luxury-50 p-8 rounded-xl border border-luxury-100 relative">
                    <div className="text-5xl text-highlight absolute -top-5 left-6">"</div>
                    <blockquote className="text-luxury-800 text-lg italic mt-4 mb-6">
                      {caseStudy.testimonial.quote}
                    </blockquote>
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-bold text-luxury-900">{caseStudy.testimonial.author}</div>
                        <div className="text-luxury-700 text-sm">{caseStudy.testimonial.position}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar - Right 1/3 */}
              <div className="space-y-8">
                {/* Technologies Used */}
                <Card className="border border-luxury-100">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-luxury-900">
                      <Layers size={20} className="text-highlight" />
                      Technologies Used
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {caseStudy.technologies.map((tech, index) => (
                        <span key={index} className="px-3 py-1 bg-luxury-50 text-luxury-800 rounded-full text-sm">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* CTA */}
                <Card className="border border-luxury-100 bg-luxury-50/70">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold mb-3 text-luxury-900">Need a similar solution?</h3>
                    <p className="text-luxury-700 mb-4">
                      Let's discuss how we can create a stunning app that delivers similar results.
                    </p>
                    <Button className="w-full bg-highlight hover:bg-highlight-dark text-white">
                      Start Your Project
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Next/Previous Case Studies Navigation */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center text-luxury-900">Explore More Case Studies</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {previousCaseStudy && (
                <Link to={previousCaseStudy.link} className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-luxury transition-all border border-luxury-100" onClick={() => window.scrollTo(0, 0)}>
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={previousCaseStudy.imageUrl}
                        alt={previousCaseStudy.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/placeholder.svg';
                        }}
                      />
                    </div>
                    <div className="flex-grow">
                      <div className="text-sm text-luxury-600 mb-1">Previous Case Study</div>
                      <h3 className="text-lg font-bold text-luxury-900 group-hover:text-highlight transition-colors">
                        {previousCaseStudy.title}
                      </h3>
                    </div>
                    <ArrowLeft className="h-6 w-6 text-luxury-400 group-hover:text-highlight transition-colors" />
                  </div>
                </Link>
              )}

              {nextCaseStudy && (
                <Link to={nextCaseStudy.link} className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-luxury transition-all border border-luxury-100" onClick={() => window.scrollTo(0, 0)}>
                  <div className="flex items-center gap-4">
                    <ArrowRight className="h-6 w-6 text-luxury-400 group-hover:text-highlight transition-colors" />
                    <div className="flex-grow text-right">
                      <div className="text-sm text-luxury-600 mb-1">Next Case Study</div>
                      <h3 className="text-lg font-bold text-luxury-900 group-hover:text-highlight transition-colors">
                        {nextCaseStudy.title}
                      </h3>
                    </div>
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={nextCaseStudy.imageUrl}
                        alt={nextCaseStudy.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/placeholder.svg';
                        }}
                      />
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </section>

        <CtaSection title="Ready to Transform Your" highlightText="Digital Presence?" description="Let's discuss how we can create a stunning app that elevates your brand and delights your users." primaryButtonText="Get Started Today" primaryButtonLink="/contact" secondaryButtonText="Learn About Our Process" secondaryButtonLink="/services/process" />
      </main>
    </div>
  );
};

export default CaseStudy;
